import _defineProperty from '../../../@babel/runtime/helpers/esm/defineProperty.js';
import _typeof from '../../../@babel/runtime/helpers/esm/typeof.js';
import _classCallCheck from '../../../@babel/runtime/helpers/esm/classCallCheck.js';
import _createClass from '../../../@babel/runtime/helpers/esm/createClass.js';
import _get from '../../../@babel/runtime/helpers/esm/get.js';
import _inherits from '../../../@babel/runtime/helpers/esm/inherits.js';
import _possibleConstructorReturn from '../../../@babel/runtime/helpers/esm/possibleConstructorReturn.js';
import _getPrototypeOf from '../../../@babel/runtime/helpers/esm/getPrototypeOf.js';
import '../../../lit-html/lit-html.js';
import { Cache } from './Cache.js';
import { transform } from './transform.js';
import { registerElement, defineScopedElement } from './registerElement.js';
import { shadyTemplateFactory } from './shadyTemplateFactory.js';
import { dedupeMixin } from '../../dedupe-mixin/src/dedupeMixin.js';
import { TemplateResult } from '../../../lit-html/lib/template-result.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }
function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

/**
 * @typedef {import('./types').ScopedElementsMixin} ScopedElementsMixin
 * @typedef {import('./types').ScopedElementsMap} ScopedElementsMap
 * @typedef {import("lit-element").LitElement} LitElement
 * @typedef {import('lit-html/lib/shady-render').ShadyRenderOptions} ShadyRenderOptions
 * @typedef {function(TemplateResult, Element|DocumentFragment|ShadowRoot, ShadyRenderOptions): void} RenderFunction
 */

/**
 * Template caches
 *
 * @type {WeakMap<Function, Cache<TemplateStringsArray, TemplateStringsArray>>}
 */
var templateCaches = new WeakMap();

/**
 * Retrieves or creates a templateCache for a specific key
 *
 * @param {Function} key
 * @returns {Cache<TemplateStringsArray, TemplateStringsArray>}
 */
var getTemplateCache = function getTemplateCache(key) {
  if (!templateCaches.has(key)) {
    // @ts-ignore
    templateCaches.set(key, new Cache(templateCaches.get(key.constructor)));
  }
  return templateCaches.get(key);
};

/**
 * Tags caches
 *
 * @type {WeakMap<object, Cache<string, string>>}
 */
var tagsCaches = new WeakMap();

/**
 * Retrieves or creates a tagsCache for a specific key
 * @param {object} key
 * @returns {Cache<string, string>}
 */
var getTagsCache = function getTagsCache(key) {
  if (!tagsCaches.has(key)) {
    tagsCaches.set(key, new Cache(tagsCaches.get(key.constructor)));
  }
  return tagsCaches.get(key);
};

/**
 * Transforms an array of TemplateResults or arrays into another one with resolved scoped elements
 *
 * @param {ReadonlyArray} items
 * @param {ScopedElementsMap} scopedElements
 * @param {Cache<TemplateStringsArray, TemplateStringsArray>} templateCache
 * @param {Cache<string, string>} tagsCache
 * @returns {ReadonlyArray}
 */
var transformArray = function transformArray(items, scopedElements, templateCache, tagsCache) {
  return items.map(function (value) {
    if (value instanceof TemplateResult) {
      return transformTemplate(value, scopedElements, templateCache, tagsCache);
    }
    if (Array.isArray(value)) {
      return transformArray(value, scopedElements, templateCache, tagsCache);
    }
    return value;
  });
};

/**
 * Transforms a TemplateResult into another one with resolved scoped elements
 *
 * @param {TemplateResult} template
 * @param {ScopedElementsMap} scopedElements
 * @param {Cache<TemplateStringsArray, TemplateStringsArray>} templateCache
 * @param {Cache<string, string>} tagsCache
 * @returns {TemplateResult}
 */
var transformTemplate = function transformTemplate(template, scopedElements, templateCache, tagsCache) {
  return new TemplateResult(transform(template.strings, scopedElements, templateCache, tagsCache), transformArray(template.values, scopedElements, templateCache, tagsCache), template.type, template.processor);
};

/**
 * Gets an instance of the ScopedElementsTemplateFactory
 *
 * @param {string} scopeName
 * @param {ScopedElementsMap} scopedElements
 * @param {Cache<TemplateStringsArray, TemplateStringsArray>} templateCache
 * @param {Cache<string, string>} tagsCache
 * @returns {function(any): any}
 */
var scopedElementsTemplateFactory = function scopedElementsTemplateFactory(scopeName, scopedElements, templateCache, tagsCache) {
  return function (template) {
    var newTemplate = transformTemplate(template, scopedElements, templateCache, tagsCache);
    return shadyTemplateFactory(scopeName)(newTemplate);
  };
};

/** @type {ScopedElementsMixin} */
var ScopedElementsMixinImplementation = function ScopedElementsMixinImplementation(superclass) {
  return /*#__PURE__*/function (_superclass) {
    _inherits(ScopedElementsHost, _superclass);
    var _super = _createSuper(ScopedElementsHost);
    function ScopedElementsHost() {
      _classCallCheck(this, ScopedElementsHost);
      return _super.apply(this, arguments);
    }
    _createClass(ScopedElementsHost, [{
      key: "defineScopedElement",
      value:
      /**
       * Defines a scoped element
       *
       * @param {string} tagName
       * @param {typeof HTMLElement} klass
       */
      function defineScopedElement$1(tagName, klass) {
        return defineScopedElement(tagName, klass, getTagsCache(this));
      }

      /**
       * Returns a scoped tag name
       *
       * @deprecated Please, use the instance method instead of the static one. This static method is not able to
       * obtain the tagName of lazy defined elements, while the instance one is.
       * @param {string} tagName
       * @returns {string|undefined}
       */
    }, {
      key: "getScopedTagName",
      value:
      /**
       * Returns a scoped tag name
       *
       * @param {string} tagName
       * @returns {string|undefined}
       */
      function getScopedTagName(tagName) {
        // @ts-ignore
        var klass = this.constructor.scopedElements[tagName];
        return klass ? registerElement(tagName, klass, getTagsCache(this)) : getTagsCache(this).get(tagName);
      }
    }], [{
      key: "scopedElements",
      get:
      /**
       * Obtains the scoped elements definitions map
       *
       * @returns {ScopedElementsMap}
       */
      function get() {
        return {};
      }

      /** @override */
    }, {
      key: "render",
      value: function render(template, container, options) {
        if (!options || _typeof(options) !== 'object' || !options.scopeName) {
          throw new Error('The `scopeName` option is required.');
        }
        var scopeName = options.scopeName,
          eventContext = options.eventContext;
        var templateCache = getTemplateCache(eventContext);
        var tagsCache = getTagsCache(eventContext);
        var scopedElements = this.scopedElements;
        return _get(_getPrototypeOf(ScopedElementsHost), "render", this).call(this, template, container, _objectSpread(_objectSpread({}, options), {}, {
          templateFactory: scopedElementsTemplateFactory(scopeName, scopedElements, templateCache, tagsCache)
        }));
      }
    }, {
      key: "getScopedTagName",
      value: function getScopedTagName(tagName) {
        // @ts-ignore
        var klass = this.scopedElements[tagName];
        return klass ? registerElement(tagName, klass, getTagsCache(this)) : getTagsCache(this).get(tagName);
      }
    }]);
    return ScopedElementsHost;
  }(superclass);
};
var ScopedElementsMixin = dedupeMixin(ScopedElementsMixinImplementation);

export { ScopedElementsMixin };
